import React, { useEffect, useState } from "react";
import { schApi } from "@/lib/schApi";
import { pillarApi } from "@/lib/pillarApi";

const TZ = "America/New_York";
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { timeZone: TZ }) : "—");
const time = (iso) => new Date(iso).toLocaleTimeString("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit" });
const words = (s) => (s ? String(s).replace(/_/g, " ") : "—");
const labelCls = "font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted mb-2";
const PILLAR = { PILLAR_1_INNER: "Inner Strength", PILLAR_2_VOCATION: "Vocation", PILLAR_3_SPIRIT: "Spiritual Counseling", PILLAR_4_DAILY: "Daily Touchpoint" };

const Section = ({ title, testId, children }) => (
  <div data-testid={testId} className="space-y-2"><div className={labelCls}>{title}</div>{children}</div>
);
const None = ({ testId, children }) => <p data-testid={testId} className="text-sm text-nex-muted">{children}</p>;

const Homework = ({ h }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`lifeskills-hw-${h.id}`} className="border border-white/10">
      <button data-testid={`lifeskills-hw-toggle-${h.id}`} onClick={() => setOpen((o) => !o)}
        className="w-full text-left p-2 hover:bg-white/[0.03] transition-colors">
        <div className="text-sm text-nex-silver">{h.title}</div>
        <div className="text-xs text-nex-muted">{PILLAR[h.pillar_id] || h.pillar_id} · week {h.program_week ?? "—"} · {words(h.status)} · assigned {fmt(h.assigned_at)}{h.verified_at ? ` · verified ${fmt(h.verified_at)}` : ""}</div>
      </button>
      {open && (
        <div data-testid={`lifeskills-hw-detail-${h.id}`} className="px-3 pb-3 space-y-1 text-xs text-nex-muted">
          {(h.steps || []).length ? <ul className="space-y-0.5">{h.steps.map((s, i) => <li key={i}>• {s}</li>)}</ul> : <p>{h.description}</p>}
          <p><span className="text-nex-silver">Resident reflection:</span> {h.reflection_notes || "—"}</p>
          {h.staff_response && <p><span className="text-nex-silver">Staff notes:</span> {h.staff_response}</p>}
          {h.verified_by?.name && <p>Verified by {h.verified_by.name}</p>}
        </div>
      )}
    </div>
  );
};

export default function LifeSkillsTab({ resident }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    Promise.all([
      schApi.get("/events", { resident_id: resident.id, event_type: "life_skills", status: "active,completed,cancelled" }).catch(() => ({ events: [] })),
      pillarApi.get(`/residents/${resident.id}/life-skills`).catch(() => ({ progress: [] })),
      pillarApi.get(`/residents/${resident.id}/pillar-summary`).catch(() => ({ homework: [] })),
    ]).then(([s, p, h]) => setD({ sessions: [...s.events].reverse(), progress: p.progress, homework: h.homework || [] }));
  }, [resident.id]);
  if (!d) return <None testId="lifeskills-loading">Loading…</None>;
  return (
    <div data-testid="lifeskills-tab" className="space-y-6">
      <p className="text-[11px] text-nex-muted">Read-only · sessions from the Scheduler, curriculum and homework from the Pillar Engine</p>
      <Section title={`Life Skills sessions · ${d.sessions.length}`} testId="lifeskills-sessions">
        {d.sessions.length ? d.sessions.map((e) => (
          <div key={e.id} data-testid={`lifeskills-session-${e.id}`} className="text-sm text-nex-silver border-b border-white/5 py-1">
            {e.title}<div className="text-xs text-nex-muted">{fmt(e.start_time)} · {time(e.start_time)}–{time(e.end_time)} ET · {e.status}</div>
          </div>
        )) : <None testId="lifeskills-sessions-empty">No Life Skills sessions in the Scheduler.</None>}
      </Section>
      <Section title={`Curriculum progress · Vocation pillar · ${d.progress.length}`} testId="lifeskills-progress">
        {d.progress.length ? d.progress.map((p) => (
          <div key={p.id} data-testid={`lifeskills-chapter-${p.chapter_id}`} className="text-sm text-nex-silver border-b border-white/5 py-1">
            Chapter {p.chapter_id} · {words(p.phase_id)} · {words(p.status)}
            <div className="text-xs text-nex-muted">{(p.topics_checked || []).length} topics checked · completed {fmt(p.completed_at)} {p.completed_by ? `by ${p.completed_by}` : ""}</div>
          </div>
        )) : <None testId="lifeskills-progress-empty">No curriculum progress yet.</None>}
      </Section>
      <Section title={`Pillar homework · ${d.homework.length}`} testId="lifeskills-homework">
        {d.homework.length ? d.homework.map((h) => <Homework key={h.id} h={h} />)
          : <None testId="lifeskills-homework-empty">No homework in the Pillar Engine.</None>}
      </Section>
    </div>
  );
}
